import React, { useState, useEffect } from 'react';
import { useBalance } from '../context/BalanceContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Star, X, Loader, AlertTriangle, Wallet } from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';

const StartPopup = ({ isOpen, onClose, appData, ratingId }) => {
    const { totalBalance, fetchBalance } = useBalance();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { token } = useAuth();

    const selectedApp = appData;

    const handleSubmit = async () => {
        if (!ratingId) {
            setFetchError('No rating to submit.');
            return;
        }
        setIsSubmitting(true);
        setFetchError(null);
        try {
            await axios.post(`/api/ratings/submit-pending/${ratingId}`, {}, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            await fetchBalance();
            onClose(true, appData);
        } catch (err) {
            setFetchError(`Submission failed: ${err.response?.data?.message || err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleTopupBalance = () => {
    onClose(false);
    navigate('/deposit');
  };

  if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
                    onClick={() => onClose(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                        className="bg-gray-800/80 backdrop-blur-2xl border border-white/10 rounded-3xl shadow-2xl w-full max-w-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex justify-between items-center p-6 border-b border-white/10">
                            <h2 className="text-xl font-bold text-slate-100">Rating Submission</h2>
                            <button onClick={() => onClose(false)} className="text-slate-400 hover:text-white transition-colors">
                                <X />
                            </button>
             </div>

                        {/* Body */}
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-6">
                                <img
                                    className="h-20 w-20 object-cover rounded-xl shadow-lg ring-1 ring-white/10"
                  src={selectedApp.image}
                  alt={selectedApp.name}
                />
              <div>
                                    <h3 className="text-2xl font-bold text-white">{selectedApp.name}</h3>
                                    <div className="flex items-center gap-1 text-yellow-400 mt-1">
                                        <Star size={16} fill="currentColor" />
                                        <span className="font-semibold">{selectedApp.rating || 'N/A'}</span>
                                        <span className="text-slate-400 text-sm ml-2">({selectedApp.reviews || 'N/A'} reviews)</span>
              </div>
            </div>
          </div>

                            <div className="grid grid-cols-3 gap-4 text-center mb-6">
                                <Stat title="Total Amount" value={selectedApp.amount?.toFixed(2)} unit="USDT" />
                                <Stat title="Total Profit" value={selectedApp.profit?.toFixed(2)} unit="USDT" />
                                <Stat title="Downloads" value={selectedApp.downloads} />
          </div>

                            <div className="text-sm space-y-2 text-slate-300 border-t border-white/10 pt-4">
                                <InfoRow label="Creation Time" value={selectedApp.creationTime ? new Date(selectedApp.creationTime).toLocaleString() : 'N/A'} />
                                <InfoRow label="Rating No." value={selectedApp.rating || 'N/A'} />
                            </div>
                        </div>

                        {/* Footer / Actions */}
                        <div className="p-6 bg-black/20 rounded-b-3xl">
                             {fetchError && (
                                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 p-3 rounded-lg mb-4">
                                    <AlertTriangle size={18} />
                                    <span>{fetchError}</span>
                                </div>
                            )}
                            
                            {totalBalance < (selectedApp.amount || 0) ? (
                                <motion.button
                    onClick={handleTopupBalance}
                                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold text-base py-3 rounded-xl shadow-lg shadow-orange-500/20 flex items-center justify-center gap-2"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                 >
                                    <Wallet size={18} />
                                    Less Balance - Please Make Deposit
                                </motion.button>
            ) : (
                                <motion.button
                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-base py-3 rounded-xl shadow-lg shadow-cyan-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {isSubmitting ? <><Loader className="animate-spin" /> Submitting...</> : 'Submit'}
                                </motion.button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

// Helper components for clean layout
const Stat = ({ title, value, unit }) => (
    <div>
        <p className="text-xs text-slate-400 uppercase tracking-wider">{title}</p>
        <p className="text-xl font-semibold text-white mt-1">
            {unit && <span className="text-sm text-cyan-400 font-normal mr-1">{unit}</span>}
            {value || 'N/A'}
        </p>
    </div>
);

const InfoRow = ({ label, value }) => (
    <div className="flex justify-between items-center">
        <p className="text-slate-400">{label}</p>
        <p className="font-medium text-slate-200">{value}</p>
    </div>
);

export default StartPopup;